import type {
  BrokerOutreachCardDto,
  BrokerOutreachSentVia,
} from "@/api/brokerOutreach";

/**
 * Every fixed sentence the Broker intro tab's cards show (D24–D33), in one
 * place, so the tests pin the words the consultant reads rather than a copy of
 * them.
 *
 * <p>🔴 <b>Every card is copy → send it yourself → click to log (R4).</b> The
 * line a card shows between the copy and the log names the one button that
 * records the send — copying writes nothing, so a consultant who copies and
 * walks away has recorded nothing, and the card says so until they click.</p>
 */
export const brokerOutreachCopy = {
  /** WSA — shown where the listing-link line would be when there is none. */
  noListingLink: "No listing link on file — add the listing URL on the listing's Source card.",
  copiedRecordSend: "Copied — click RECORD SEND once you've sent it.",
  copiedLog: "Copied — click LOG once you've sent it.",
  copiedSubmitted: "Copied — click I SUBMITTED IT once you've submitted the form.",

  /** D27 — a guide, not a block. */
  textTarget: (n: number) => `Aim for about ${n} characters — a guide, not a limit.`,
  /** D28 — copy is blocked while the draft is over the limit. */
  overLimit: (over: number) =>
    `${over} ${over === 1 ? "character" : "characters"} over the form's limit — shorten it before copying.`,
  limitBelowMinimum: (min: number) => `The limit can't be below ${min} characters.`,

  /** D33 — the follow-up email's length control. */
  overWords: (max: number) => `Keep a follow-up to ${max} words or fewer.`,

  /** R8 — said on the cards it applies to, because the email card above them
   *  carries the statement and a reader would otherwise expect it here too. */
  noRoleStatement:
    "No role statement is added to this message — it goes out as written.",

  noContactForm: "No contact form on file for this listing.",
  findAgentHint: "Find the agent's details in a new tab, then come back — this strip refreshes when you do.",
} as const;

/** D25 — how a card's latest send went, as the status line names it. */
export const SENT_VIA_LABEL: Record<BrokerOutreachSentVia, string> = {
  AppSend: "app send",
  OwnEmail: "own email",
  CopiedText: "copied text",
  WebForm: "web form",
};

/**
 * The words around the timestamp in "Sent {when} by {who} via {how}" — the
 * timestamp itself is a component, so the line is composed in two halves.
 * Returns e.g. "by Dana Reyes via own email".
 */
export function sentByVia(card: Pick<BrokerOutreachCardDto, "sentByName" | "sentVia">): string {
  const parts: string[] = [];
  if (card.sentByName?.trim()) parts.push(`by ${card.sentByName.trim()}`);
  if (card.sentVia) parts.push(`via ${SENT_VIA_LABEL[card.sentVia]}`);
  return parts.join(" ");
}

/**
 * R13 — who the message was WRITTEN AS, named only when it differs from who
 * logged it. Null when they are the same person or nothing was recorded (a
 * Phoenix send records no written-as sender).
 */
export function writtenAsNote(
  card: Pick<BrokerOutreachCardDto, "sentByName" | "writtenAsName">,
): string | null {
  const writtenAs = card.writtenAsName?.trim();
  if (!writtenAs) return null;
  if (writtenAs === card.sentByName?.trim()) return null;
  return `Written as ${writtenAs}`;
}

/** The word count the follow-up email's 120-word control measures — the same
 *  whitespace split the server's guard uses. */
export function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/**
 * Whether what went out is the current draft.
 *
 * <p>A Phoenix send records the draft plus the listing-link line (WSA); a RECORD
 * SEND records what the copy put on the clipboard (R4) — the draft, the
 * listing-link line, the sign-off ("\n\n--\n…") and the role statement. All
 * are "the draft as it stands", so none may claim the draft "has been edited
 * since this went out". The line is recognised by `listingLinkLead` (its wording
 * before the URL), so a line whose URL has since changed still matches. Anything
 * else between the draft and the role statement — a paragraph deleted from the
 * draft after the send — is a real edit and still reads as one.</p>
 */
export function sentMatchesDraft(
  sentBody: string,
  bodyDraft: string,
  roleStatement: string,
  listingLinkLead?: string | null,
): boolean {
  const sent = sentBody.trim();
  const draft = bodyDraft.trim();
  if (sent === draft) return true;
  if (!sent.startsWith(draft)) return false;
  let rest = sent.slice(draft.length);
  const lead = listingLinkLead?.trim();
  if (lead && rest.startsWith(`\n\n${lead}`)) {
    const lineEnd = rest.indexOf("\n", 2);
    rest = lineEnd < 0 ? "" : rest.slice(lineEnd);
    if (rest === "") return true;
  }
  const role = roleStatement.trim();
  if (rest === `\n\n${role}`) return true;
  return rest.startsWith("\n\n--\n") && rest.endsWith(`\n\n${role}`);
}
